import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomSearchBar from "../Lease Module/CustomSearchBar";
import CustomBottomNav from "../Rent Module/BottomNavbar/BottomNavbar";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const REQUEST_API = "https://landnest.net:81/property-request/";

const TYPES = ["JV/JD", "BUILD TO SUIT"];
const PROPERTY_TYPES = ["PLOT", "LAND"];

// 🔑 Normalize API value → UI value
const normalizeLookingFor = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === "jv/jd") return "JV/JD";
  if (v === "build to suit") return "BUILD TO SUIT";
  return null;
};

const JDJV = () => {
  const navigate = useNavigate();

  // 🔒 Parent always active
  const [selectedType, setSelectedType] = useState("JV/JD");
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [visibleRequests, setVisibleRequests] = useState([]);

  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [activeReqId, setActiveReqId] = useState(null);
  const [currentBounds, setCurrentBounds] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  const mapContainerRef = useRef(null);
  const cardListRef = useRef(null);

  // ------------------------------------------------------
  // FETCH DATA
  // ------------------------------------------------------
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await axios.get(REQUEST_API);

        const parsed = res.data.flatMap((item) => {
          const lookingFor = normalizeLookingFor(item.looking_for);
          if (!lookingFor || !Array.isArray(item.pro_loc)) return [];

          return item.pro_loc.map((loc) => ({
            id: `${item.req_id}-${loc.loc_id}`, // marker id
            parentReqId: String(item.req_id),   // card id
            looking_for: lookingFor,
            property_type: item.property_type?.toUpperCase(),
            min_budget: item.min_budget,
            max_budget: item.max_budget,
            comment: item.comment,
            location_name: loc.location,
            lat: parseFloat(loc.lat),
            lng: parseFloat(loc.long),
          }));
        });

        setRequests(parsed);
        setFilteredRequests(parsed);
      } catch (e) {
        console.error("API error", e);
      }
    };

    fetchRequests();
  }, []);

  // ------------------------------------------------------
  // FORCE RESET SUB FILTER WHEN PARENT CHANGES
  // ------------------------------------------------------
  useEffect(() => {
    setSelectedPropertyType(null);
  }, [selectedType]);

  // ------------------------------------------------------
  // APPLY FILTERS
  // ------------------------------------------------------
  useEffect(() => {
    let filtered = requests.filter(
      (r) => r.looking_for === selectedType
    );

    if (selectedPropertyType) {
      filtered = filtered.filter(
        (r) => r.property_type === selectedPropertyType
      );
    }

    setFilteredRequests(filtered);
  }, [requests, selectedType, selectedPropertyType]);

  // ------------------------------------------------------
  // MAP INIT
  // ------------------------------------------------------
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const leafletMap = L.map(mapContainerRef.current, {
      center: [17.385, 78.486],
      zoom: 11,
      zoomControl: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
      leafletMap
    );

    L.control.zoom({ position: "topright" }).addTo(leafletMap);

    leafletMap.on("moveend", () => {
      setCurrentBounds(leafletMap.getBounds());
    });

    setMap(leafletMap);
    setMapLoaded(true);
    setCurrentBounds(leafletMap.getBounds());

    return () => leafletMap.remove();
  }, [mapKey]);

  // ------------------------------------------------------
  // MAP BOUNDS FILTER
  // ------------------------------------------------------
  useEffect(() => {
    if (!currentBounds) {
      setVisibleRequests(filteredRequests);
      return;
    }

    setVisibleRequests(
      filteredRequests.filter((p) =>
        currentBounds.contains(L.latLng(p.lat, p.lng))
      )
    );
  }, [currentBounds, filteredRequests]);

  // ------------------------------------------------------
  // MARKERS
  // ------------------------------------------------------
  useEffect(() => {
    if (!mapLoaded || !map) return;

    markers.forEach((m) => map.removeLayer(m));

    const newMarkers = [];

    filteredRequests.forEach((req) => {
      const marker = L.marker([req.lat, req.lng]).addTo(map);

      marker.on("click", () => {
        setActiveReqId(req.parentReqId);
        const el = cardListRef.current?.querySelector(
          `[data-id="${req.parentReqId}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", inline: "center" });
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
  }, [filteredRequests, mapLoaded]);

  // ------------------------------------------------------
  // UI
  // ------------------------------------------------------
  return (
    <Box sx={{ pb: 7, maxWidth: 480, mx: "auto", bgcolor: "#EFE7DD" }}>
      <Box sx={{ px: 1, py: 1 }}>
        <CustomSearchBar hidePriceFilter />
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography fontWeight={600}>Looking For</Typography>

        <Box sx={{ display: "flex", gap: 1, py: 1 }}>
          {TYPES.map((type) => (
            <Chip
              key={type}
              label={type}
              onClick={() => setSelectedType(type)}
              sx={{
                bgcolor: selectedType === type ? "#000" : "transparent",
                color: selectedType === type ? "#fff" : "#000",
                border: "1px solid black",
                fontWeight: "bold",
                pointerEvents: selectedType === type ? "none" : "auto",
              }}
            />
          ))}

          <Typography fontWeight={600}>:</Typography>

          {PROPERTY_TYPES.map((ptype) => (
            <Chip
              key={ptype}
              label={ptype}
              onClick={() =>
                setSelectedPropertyType(
                  selectedPropertyType === ptype ? null : ptype
                )
              }
              sx={{
                bgcolor:
                  selectedPropertyType === ptype ? "#000" : "transparent",
                color:
                  selectedPropertyType === ptype ? "#fff" : "#000",
                border: "1px solid black",
                fontWeight: "bold",
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 10, position: "relative" }}>
        <div
          ref={mapContainerRef}
          key={mapKey}
          style={{
            width: "100vw",
            height: "calc(100vh - 260px)",
            marginLeft: "-16px",
          }}
        />

        {/* CAROUSEL CARDS */}
        {visibleRequests.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: -10,
              left: 0,
              right: 0,
              zIndex: 1001,
              display: "flex",
            }}
          >
            <Box
              ref={cardListRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                px: 1,
                scrollSnapType: "x mandatory",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {visibleRequests.map((req) => (
                <Box
                  key={req.id}
                  data-id={req.id}
                  sx={{
                    flex: "0 0 280px",
                    scrollSnapAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  {/* <div style={{ padding: 6 }}>
                    <BankAuctionReusableCard
                      property={{
                        title: req.looking_for,
                        property_type: req.property_type,
                        location: req.comment || "Request",
                        price: req.min_budget,
                      }}
                      onClick={() => navigate(`/property-request/${req.id}`)}
                    />
                  </div> */}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>


      <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <CustomBottomNav />
      </Paper>
    </Box>
  );
};

export default JDJV;
